import {
  CursorClick02,
  MusicNote01,
  Rocket02,
  Send01,
  Star01,
  Users01,
} from "@untitledui/icons"

import { StatCard } from "@/components/dashboard/stat-card"
import { Link } from "@/components/ui/link"
import { WarmStartLedgerTable } from "@/components/warm-starts/ledger-table"
import { adminUsersPath, adminWarmStartsPath, playlistsPath } from "@/routes"
import type { WarmStartLedgerRow } from "@/types"

export interface AdminMetrics {
  warm_starts: { created: number; claimed: number; published: number }
  content: {
    published_playlists: number
    published_creators: number
    members: number
  }
}

interface AdminBandProps {
  metrics: AdminMetrics
  seeds: WarmStartLedgerRow[]
}

// The admin-only growth band. Streamed in as a deferred prop, so it carries its
// own skeleton (below) while the metric queries run.
export function AdminBand({ metrics, seeds }: AdminBandProps) {
  return (
    <section className="border-secondary flex flex-col gap-6 border-t pt-8">
      <div className="flex flex-col gap-1">
        <p className="text-tertiary text-xs font-semibold tracking-wide uppercase">
          Growth · Admin
        </p>
        <h2 className="text-primary text-lg font-semibold">Invites funnel</h2>
        <p className="text-tertiary text-sm">
          From the first invite sent to published work.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={Send01}
          color="brand"
          label="Invites created"
          value={metrics.warm_starts.created}
          href={adminWarmStartsPath()}
        />
        <StatCard
          icon={CursorClick02}
          label="Claimed"
          value={metrics.warm_starts.claimed}
          href={adminWarmStartsPath()}
        />
        <StatCard
          icon={Rocket02}
          color="success"
          label="Published"
          value={metrics.warm_starts.published}
          href={adminWarmStartsPath()}
        />
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="text-primary text-lg font-semibold">Community</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            icon={MusicNote01}
            label="Published playlists"
            value={metrics.content.published_playlists}
            href={playlistsPath()}
          />
          <StatCard
            icon={Star01}
            label="Creators"
            value={metrics.content.published_creators}
          />
          <StatCard
            icon={Users01}
            label="Members"
            value={metrics.content.members}
            href={adminUsersPath()}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-primary text-lg font-semibold">Recent invites</h2>
          <Link href={adminWarmStartsPath()} className="text-sm font-semibold">
            View all
          </Link>
        </div>

        {seeds.length > 0 ? (
          <WarmStartLedgerTable rows={seeds} aria-label="Recent invites" />
        ) : (
          <p className="text-tertiary border-secondary rounded-xl border border-dashed p-6 text-center text-sm">
            No invites yet. Compose one to turn a conversation into published
            work.
          </p>
        )}
      </div>
    </section>
  )
}

// Shown while the deferred admin props load. Mirrors the band's silhouette so
// the layout doesn't jump when the real data arrives.
export function AdminBandSkeleton() {
  return (
    <section
      aria-hidden="true"
      className="border-secondary flex flex-col gap-6 border-t pt-8"
    >
      <div className="bg-secondary h-5 w-44 animate-pulse rounded motion-reduce:animate-none" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="border-secondary bg-primary h-28 animate-pulse rounded-xl border motion-reduce:animate-none"
          />
        ))}
      </div>
    </section>
  )
}
